import type { FC, PropsWithChildren } from 'react';
import { Box, TabPanel, TabPanels, TabsProvider } from 'braid-design-system';
import { StringParam, useQueryParam } from 'use-query-params';
import Profile from '../Profile';
import ProspectEmail from '../../ProspectEmail';
import { observer } from 'mobx-react';
import type { IJobApplication } from '../../../../stores/JobApplicationStore/JobApplication';
import { JOB_APPLICATION_TAB } from '../../../../constants/tabs';
import {
  trackClickedNotesTab,
  trackClickedProfileTab,
  trackClickedEmailsTab,
  trackClickedResumeTab,
  trackClickedCoverLetterTab,
  trackClickedSelectionCriteriaTab,
  trackClickedPreviousApplicationsTab,
} from 'src/utils/events';
import { sendHotjarTag } from 'src/utils/hotjar';
import { HOTJAR_TAG_PROFILE } from '../../../../constants/hotjar';
import PdfViewer from '../../PdfViewer';
import PreviousApplications from '../../PreviousApplications';
import TabSelect, { type TabSelectOption } from '../TabSelect';
import NotesAttachments from '../../NotesAttachments';
import type { IAttachment } from 'src/stores/JobApplicationStore/Attachment';
import { CANDIDATE_PROFILE_DRAWER_TAB_PARAM_KEY } from '../../../../pages/CandidateManagementPage/queryParams';
import { useGetDetailsTabs } from './hooks/useGetDetailsTabs';
import { TRACKING_CANDIDATE_DETAILS } from 'src/constants/pageTrackingData';
import type { Locale as MelwaysLocale } from '@seek/melways-sites';
import { convertToIsVerifiedId } from 'src/utils/eventMappers';

interface Props {
  jobApplication: IJobApplication;
  isMobile: boolean;
  locale: MelwaysLocale;
  questionnaireId?: string;
}

export interface DetailTabSelectOption extends TabSelectOption {
  attachment?: IAttachment;
  counts?: number;
  value: JOB_APPLICATION_TAB;
}

const DetailsTabView = ({
  jobApplication,
  isMobile,
  locale,
  questionnaireId,
}: Props) => {
  const {
    candidateId,
    notes,
    attachmentsStore,
    prospectEmailStore,
    candidateStore,
  } = jobApplication;

  const isVerifiedId = convertToIsVerifiedId(jobApplication.profile);

  const [, setTabQueryParam] = useQueryParam(
    CANDIDATE_PROFILE_DRAWER_TAB_PARAM_KEY,
    StringParam,
  );
  const tabs = useGetDetailsTabs(jobApplication, isMobile);
  const getDocumentTrackingAttributes = (tab: JOB_APPLICATION_TAB) => {
    const doc = tabs.find((x) => x.value === tab)?.attachment;
    return {
      documentType: doc?.typeName,
      attachmentId: doc?.id,
      ...jobApplication.baseTrackingAttributes,
    };
  };

  const onTabChange = (_selectedIndex: number, tab?: string) => {
    jobApplication.selectTabInDetailView(tab as JOB_APPLICATION_TAB);
    switch (tab) {
      case JOB_APPLICATION_TAB.NOTES_ATTACHMENTS:
        trackClickedNotesTab(notes.trackingAttributes, {
          currentPage: TRACKING_CANDIDATE_DETAILS,
        });
        setTabQueryParam(JOB_APPLICATION_TAB.NOTES_ATTACHMENTS);
        return;
      case JOB_APPLICATION_TAB.PROFILE:
        trackClickedProfileTab(jobApplication.baseTrackingAttributes);
        sendHotjarTag(HOTJAR_TAG_PROFILE);
        setTabQueryParam(JOB_APPLICATION_TAB.PROFILE);
        return;
      case JOB_APPLICATION_TAB.PREVIOUS_APPLICATIONS:
        trackClickedPreviousApplicationsTab(
          jobApplication.baseTrackingAttributes,
        );
        setTabQueryParam(JOB_APPLICATION_TAB.PREVIOUS_APPLICATIONS);
        return;
      case JOB_APPLICATION_TAB.EMAILS:
        trackClickedEmailsTab(prospectEmailStore.trackingAttributes);
        setTabQueryParam(JOB_APPLICATION_TAB.EMAILS);
        return;
      case JOB_APPLICATION_TAB.RESUME:
        trackClickedResumeTab(getDocumentTrackingAttributes(tab));
        setTabQueryParam(JOB_APPLICATION_TAB.RESUME);
        return;
      case JOB_APPLICATION_TAB.COVER_LETTER:
        trackClickedCoverLetterTab(getDocumentTrackingAttributes(tab));
        setTabQueryParam(JOB_APPLICATION_TAB.COVER_LETTER);
        return;
      case JOB_APPLICATION_TAB.SELECTION_CRITERIA:
        trackClickedSelectionCriteriaTab(getDocumentTrackingAttributes(tab));
        setTabQueryParam(JOB_APPLICATION_TAB.SELECTION_CRITERIA);
        return;
      default:
        throw new Error('Unknown tab name');
    }
  };

  const getTabPanel = (tab: DetailTabSelectOption) => {
    switch (tab.value) {
      case JOB_APPLICATION_TAB.PROFILE:
        return (
          <DetailsContentViewWrapper>
            <Profile
              questionnaireId={questionnaireId}
              jobApplication={jobApplication}
            />
          </DetailsContentViewWrapper>
        );
      case JOB_APPLICATION_TAB.RESUME:
      case JOB_APPLICATION_TAB.COVER_LETTER:
      case JOB_APPLICATION_TAB.SELECTION_CRITERIA:
        const attachment = tab.attachment;
        if (attachment && attachment.jobApplication) {
          return (
            <DetailsContentViewWrapper>
              <PdfViewer
                candidateId={candidateId}
                attachment={attachment}
                isVerifiedId={isVerifiedId}
              />
            </DetailsContentViewWrapper>
          );
        }
        return <TabPanel> </TabPanel>;
      case JOB_APPLICATION_TAB.PREVIOUS_APPLICATIONS:
        return (
          <DetailsContentViewWrapper>
            <PreviousApplications
              candidateStore={candidateStore}
              isMobile={isMobile}
              locale={locale}
              isVerifiedId={isVerifiedId}
            />
          </DetailsContentViewWrapper>
        );
      case JOB_APPLICATION_TAB.EMAILS:
        return (
          <DetailsContentViewWrapper>
            <ProspectEmail
              prospectEmailStore={prospectEmailStore}
              locale={locale}
            />
          </DetailsContentViewWrapper>
        );
      case JOB_APPLICATION_TAB.NOTES_ATTACHMENTS:
        return (
          <DetailsContentViewWrapper>
            <NotesAttachments
              notes={notes}
              attachmentsStore={attachmentsStore}
              locale={locale}
              isVerifiedId={isVerifiedId}
            />
          </DetailsContentViewWrapper>
        );
      default:
        return <></>;
    }
  };

  return (
    <>
      <TabsProvider
        id="tab-select-detail-view"
        selectedItem={jobApplication.selectedTabInDetailView}
        onChange={onTabChange}
      >
        <TabSelect testId="details-tab-selector" options={tabs} />
        <Box
          display="flex"
          flexDirection="column"
          flexGrow={1}
          background="surface"
        >
          <TabPanels>
            {tabs.map((tab) => (
              <TabPanel item={tab.value} key={tab.value}>
                {getTabPanel(tab)}
              </TabPanel>
            ))}
          </TabPanels>
        </Box>
      </TabsProvider>
    </>
  );
};

const DetailsContentViewWrapper: FC<PropsWithChildren> = ({ children }) => (
  <Box paddingY="medium" background="surface">
    {children}
  </Box>
);

export default observer(DetailsTabView);
